import { ApolloError } from "@apollo/client";

import { PublishContextChangesetFragment } from "../../queries/useInitPublishContextChangeset.generated";
import { CustomerEvalSuiteWithBaseVersionFragment } from "../../queries/useInitPublishContextChangesetBaseEvalFiles.generated";
import { OrgGuideFileWithBaseVersionFragment } from "../../queries/useInitPublishContextChangesetBaseGuideFiles.generated";
import { ContextWorkbenchState } from "../contextWorkbenchState";
import { ContextWorkbenchSelectorOptions } from "../selectors/options";
import { contextWorkbenchPublishSelectors } from "../selectors/publishSelectors";

export type InitPublishBaseGuideFilesPayload = {
  data: readonly OrgGuideFileWithBaseVersionFragment[] | null | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  options: ContextWorkbenchSelectorOptions;
};

export type InitPublishBaseEvalFilesPayload = {
  data: readonly CustomerEvalSuiteWithBaseVersionFragment[] | null | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  isTruncated: boolean;
};

export type InitPublishChangesetPayload = {
  data: PublishContextChangesetFragment | null | undefined;
  loading: boolean;
  error: ApolloError | undefined;
};

export type UpdatePublishChangesetMetadataPayload = {
  title: string | null;
  description: string | null;
};

export function initPublishChangeset(
  state: ContextWorkbenchState,
  payload: InitPublishChangesetPayload,
): void {
  const { data, error, loading } = payload;
  state.publish.changeset = {
    loading: loading,
    error: error,
    value: data ?? undefined,
    loadId: data?.id ?? state.publish.changeset.loadId,
  };
}

export function updatePublishChangesetMetadata(
  state: ContextWorkbenchState,
  payload: UpdatePublishChangesetMetadataPayload,
): void {
  const changeset = state.publish.changeset.value;
  if (changeset != null) {
    state.publish.changeset.value = {
      ...changeset,
      title: payload.title,
      description: payload.description,
    };
  }
}

export function initPublishBaseGuideFiles(
  state: ContextWorkbenchState,
  payload: InitPublishBaseGuideFilesPayload,
): void {
  const { data, error, loading } = payload;
  state.publish.baseGuideFiles = {
    loading: loading,
    error: error,
    value: data ?? undefined,
  };

  // auto-select the first changed file
  if (data && !state.publish.selectedFileId) {
    state.publish.selectedFileId =
      contextWorkbenchPublishSelectors.selectFirstVisibleFile(
        { contextWorkbench: state },
        payload.options,
      );
  }
}

export function initPublishBaseEvalFiles(
  state: ContextWorkbenchState,
  payload: InitPublishBaseEvalFilesPayload,
): void {
  const { data, error, isTruncated, loading } = payload;
  state.publish.baseEvalFiles = {
    loading: loading,
    error: error,
    value: data ?? undefined,
    isTruncated: isTruncated,
  };
}
